import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { LogOut } from 'lucide-react'
import { Panel, Button, Field, FormRow, Select, Checkbox, Banner, Modal, useToast } from '../../ds/index'
import { useApi } from '../../lib/useApi'
import { api, post, put } from '../../lib/api'
import { dollars } from '../../lib/format'
import { Icon } from '../../components/Icon'
import { LoadingBlock, ErrorBlock } from '../../components/Loading'
import { useCheckout, type CheckoutStart } from '../../lib/checkout'
import { useSession } from '../../lib/session'

type Business = {
  name: string; legal_name: string | null; entity_type: string | null; entity_state: string | null; category_id: string; category_name: string
  job_type_ids: string[]; roc_license: string | null; mailing_address: string | null; notice_email: string | null; website: string | null
  facebook_page: string | null; lead_contact_name: string | null; lead_contact_phone: string | null; lead_contact_email: string | null
  typed_trade?: string | null
}
/** A first tab, what it buys, and the words agreed to for it (lib/signup.js offer). */
/** tab: money plus gift in the tab; total: what the tab buys at normal lead prices, after the tab discount. */
export type DepositOffer = { cents: number; credit: number; gift: number; giftCredit: number; tab: number; total: number; offer: string[] }
export type JoinState = {
  org: { name: string; timezone: string; tab_choices: number[]; tab_min_cents: number; tab_discount_bps: number; can_save_card: boolean; has_zones: boolean; trial_days: number; trial_cap_cents: number; signup_gift_cents: number }
  deposit: { min_cents: number; choices: DepositOffer[] }
  status: 'pending' | 'active' | 'cancelled'; submitted_at: string | null; declined_at: string | null; decline_reason: string | null
  question: { question: string; created_at: string } | null
  account: { name: string; email: string; phone: string | null; email_confirmed: boolean }
  business: Business; categories: { id: string; name: string }[]; job_types: { id: string; name: string; job_tier: string | null }[]; entity_types: string[]
  zone: { id: string; status: string; zone_id: string; name: string } | null
  terms: { version_id: string; version: string; text: string; agree_label: string; sign_line: string; exhibit_c: string; card_label: string } | null
  accepted: { version: string; accepted_at: string; typed_name: string; title: string | null } | null
  card: { brand: string | null; last4: string | null } | null
  consent: { consented_at: string; tab_amount_cents: number; card_last4: string | null } | null
  tab_amount_cents: number | null
  steps: { account: boolean; business: boolean; zone: boolean; agreement: boolean; card: boolean }
}

/** Terms paragraphs, headings as headings. Plain text only: nothing in the Terms is markup. */
export function TermsText({ text }: { text: string }) {
  return (
    <div className="terms-text">
      {text.split(/\n{2,}/).map((p, i) => p.startsWith('# ') ? <h3 key={i}>{p.slice(2)}</h3>
        : p.startsWith('| ') ? <div key={i} className="terms-row">{p.split('|').map((c) => c.trim()).filter(Boolean).map((c, j) => <span key={j}>{c}</span>)}</div>
          : <p key={i}>{p}</p>)}
    </div>
  )
}

/**
 * Signing up, step 2 of 2 (step 1, JoinStart, made the login): the deposit.
 * The trial offer, the Terms box, the card authorization box, and the $100 on
 * Stripe's form. Paid, the business is active and leads start; the portal opens.
 * Everything else is finished later (Finish setup, with reminders).
 */
export default function Signup() {
  const j = useApi<JoinState>('/api/client/join')
  const { refresh } = useSession()
  const [params] = useSearchParams()
  const sessionId = params.get('session_id')
  const leave = async () => { await post('/api/auth/logout', {}); await refresh() }
  if (j.error) return <ErrorBlock error={j.error} />
  if (!j.data) return <LoadingBlock />
  const d = j.data
  if (d.status === 'cancelled') {
    return <Shell org={d.org.name} onLeave={leave}><Panel><h2 className="panel-title">This sign-up is closed</h2>{d.decline_reason && <p>{d.decline_reason}</p>}<p className="muted">Nothing more will be charged.</p></Panel></Shell>
  }
  if (params.get('paid') === '1' && sessionId) return <Shell org={d.org.name} onLeave={leave}><Confirming sessionId={sessionId} /></Shell>
  return <Shell org={d.org.name} onLeave={leave}><Deposit d={d} /></Shell>
}

function Shell({ org, children, onLeave }: { org: string; children: React.ReactNode; onLeave: () => void }) {
  return (
    <div className="join-shell">
      <div className="spread" style={{ alignItems: 'center', marginBottom: 'var(--s-6)' }}>
        <div><div className="hero-kicker">Step 2 of 2</div><h1 className="page-title" style={{ margin: 0 }}>Leads from {org}</h1></div>
        <Button variant="ghost" onClick={onLeave} aria-label="Sign out"><Icon of={LogOut} /></Button>
      </div>
      <div className="stack">{children}</div>
    </div>
  )
}

/** Back from Stripe: record the deposit (the webhook does too), then open the portal. Waits up to a minute. */
function Confirming({ sessionId }: { sessionId: string }) {
  const [late, setLate] = useState(false)
  const tries = useRef(0)
  useEffect(() => {
    let gone = false
    const check = async () => {
      tries.current += 1
      try {
        const r = await post<{ active: boolean }>('/api/client/join/deposit/confirm', { session_id: sessionId })
        if (r.active) { window.location.assign('/portal'); return }
      } catch { /* the webhook may still be on its way: keep checking */ }
      if (gone) return
      if (tries.current >= 20) { setLate(true); return }
      setTimeout(check, 3000)
    }
    check()
    return () => { gone = true }
  }, [sessionId])
  return (
    <Panel>
      <h2 className="panel-title">{late ? 'Your payment is still on its way' : 'Confirming your payment'}</h2>
      <p style={{ marginBottom: 0 }}>{late ? 'Stripe has not told us yet. It usually takes seconds. You will get an email the moment it is in, and this page opens your portal when you reload it.' : 'One moment while Stripe confirms it. Your portal opens by itself.'}</p>
      {late && <div className="row-tight" style={{ marginTop: 'var(--s-5)' }}><Button onClick={() => window.location.reload()}>Check again</Button></div>}
    </Panel>
  )
}

function Deposit({ d }: { d: JoinState }) {
  const toast = useToast()
  const [agree, setAgree] = useState(false)
  const [card, setCard] = useState(false)
  // Typed, not filled in: typing the full name is part of signing (Business Terms 16.1).
  const [name, setName] = useState('')
  const [full, setFull] = useState(false)
  const [auth, setAuth] = useState(false)
  const [more, setMore] = useState(false)
  const [busy, setBusy] = useState(false)
  const [pick, setPick] = useState<number | 'other'>(d.deposit.choices[0]?.cents ?? d.deposit.min_cents)
  const [other, setOther] = useState('')
  const typed = useTypedOffer(pick === 'other' ? other : '', d.deposit.min_cents)
  const checkout = useCheckout('Your first tab')
  if (!d.terms) return <Panel><p style={{ margin: 0 }}>{d.org.name} is finishing its Terms. Check back soon.</p></Panel>
  const okName = /\S+\s+\S+/.test(name.trim())
  const trial = d.org.trial_days > 0
  const o = pick === 'other' ? typed.offer : d.deposit.choices.find((c) => c.cents === pick) || null
  const pay = async () => {
    if (!o) return
    setBusy(true)
    try {
      // Stripe's own page: Apple Pay and Google Pay are one tap there. It comes back to the portal when paid.
      checkout.open(await post<CheckoutStart>('/api/client/join/deposit', { version_id: d.terms!.version_id, typed_name: name, agree_terms: agree, agree_card: card, deposit_cents: o.cents }), () => setBusy(false))
    } catch (e) { toast((e as Error).message, 'neg'); setBusy(false) }
  }
  return (
    <Panel>
      <div className="panel-header"><h2 className="panel-title">{trial ? `Start your ${d.org.trial_days}-day trial` : 'Your first tab'}</h2></div>
      <p style={{ marginTop: 0 }}>
        {d.org.signup_gift_cents > 0
          ? <>Pick how much to put in, {dollars(d.deposit.min_cents)} or more. <strong>We add a {dollars(d.org.signup_gift_cents)} sign-up gift</strong> on top.</>
          : <>Pick how much to put in, {dollars(d.deposit.min_cents)} or more.</>}
      </p>
      <div className="amount-chips" role="radiogroup" aria-label="Your first tab">
        {d.deposit.choices.map((c) => (
          <button key={c.cents} type="button" role="radio" aria-checked={pick === c.cents} className="amount-chip" onClick={() => setPick(c.cents)}>{dollars(c.cents)}</button>
        ))}
        <button type="button" role="radio" aria-checked={pick === 'other'} className="amount-chip" onClick={() => setPick('other')}>Other</button>
      </div>
      {pick === 'other' && (
        <div style={{ marginTop: 'var(--s-4)' }}>
          <Field label={`Amount in dollars (${dollars(d.deposit.min_cents)} or more)`} id="t-amount" inputMode="numeric" autoComplete="off" enterKeyHint="done" placeholder={String(d.deposit.min_cents / 100)}
            value={other} onChange={(e) => setOther(e.target.value.replace(/[^0-9]/g, '').slice(0, 5))} aria-invalid={Boolean(typed.error)} aria-describedby="t-amount-err" />
          {typed.error && <p className="form-error" id="t-amount-err" role="alert" style={{ marginTop: 'var(--s-2)' }}>{typed.error}</p>}
        </div>
      )}
      {o && (
        <div className="credit-sum" aria-live="polite">
          <div className="credit-row"><span>You pay today</span><span className="num">{dollars(o.cents)}</span></div>
          {o.gift > 0 && <div className="credit-row gift"><span>Sign-up gift</span><span className="num">+{dollars(o.gift)}</span></div>}
          <div className="credit-row total"><span>In your tab</span><span className="num">{dollars(o.tab)}</span></div>
          {d.org.tab_discount_bps > 0 && <p className="credit-note">Leads are {d.org.tab_discount_bps / 100}% off, so that buys <strong>{dollars(o.total)}</strong> of leads at normal prices.</p>}
        </div>
      )}
      {trial ? (
        <ul className="deposit-points" style={{ marginTop: 'var(--s-4)' }}>
          <li>{d.business.typed_trade ? <><strong>Leads start as soon as we set up your trade</strong> ({d.business.typed_trade})</> : <strong>Leads start now</strong>}, up to {dollars(d.org.trial_cap_cents)} in {d.org.trial_days} days, nothing else charged</li>
          <li><strong>Cancel any time</strong> in {d.org.trial_days} days for a full refund</li>
        </ul>
      ) : (
        <p className="muted" style={{ margin: 'var(--s-4) 0 0' }}>
          {d.business.typed_trade ? `Leads start as soon as we set up your trade (${d.business.typed_trade}).` : 'Leads start right away.'} {o ? `Then ${dollars(o.cents)} a month is charged to your card and added to your tab. You can change that any time.` : ''}
        </p>
      )}
      <button type="button" className="link-button" aria-expanded={more} onClick={() => setMore(!more)} style={{ marginTop: 'var(--s-3)' }}>{more ? 'Hide the details' : 'How billing works'}</button>
      {more && o && <ul className="deposit-offer" style={{ marginTop: 'var(--s-3)' }}>{o.offer.map((line) => <li key={line}>{line}</li>)}</ul>}
      <div className="stack" style={{ marginTop: 'var(--s-5)' }}>
        <div className="tap-row">
          <Checkbox checked={agree} onChange={(e) => setAgree(e.target.checked)}
            label={<>I have read and agree to the <button type="button" className="link-button inline" onClick={(e) => { e.preventDefault(); setFull(true) }}>Handpicked Business Terms</button></>} />
        </div>
        <div className="tap-row">
          <Checkbox checked={card} onChange={(e) => setCard(e.target.checked)}
            label={<>{d.terms.card_label} (<button type="button" className="link-button inline" onClick={(e) => { e.preventDefault(); setAuth(true) }}>how</button>)</>} />
        </div>
        <Field label="Type your full name" id="t-name" autoComplete="name" autoCapitalize="words" enterKeyHint="done" placeholder={d.account.name} value={name} onChange={(e) => setName(e.target.value)} />
        {!d.org.can_save_card && <Banner tone="warn" title="Card payments are not set up yet">{d.org.name} will set this up shortly. Check back soon.</Banner>}
        <Button onClick={pay} disabled={!o || !agree || !card || !okName || busy || !d.org.can_save_card}>{busy ? 'Opening Stripe' : 'Agree and continue'}</Button>
        <p className="sign-line" style={{ margin: 0 }}>{d.terms.sign_line} You confirm you are 18 or older and can sign for {d.business.name}. Then you pay {o ? dollars(o.cents) : 'your first tab'} on Stripe's secure page, with Apple Pay, Google Pay or a card.</p>
      </div>
      {full && <Modal title={`Handpicked Business Terms, version ${d.terms.version}`} onClose={() => setFull(false)} footer={<Button variant="secondary" onClick={() => setFull(false)}>Close</Button>}><TermsText text={d.terms.text} /></Modal>}
      {auth && <Modal title="Your card authorization" onClose={() => setAuth(false)} footer={<Button variant="secondary" onClick={() => setAuth(false)}>Close</Button>}><TermsText text={d.terms.exhibit_c} /></Modal>}
      {checkout.element}
    </Panel>
  )
}

/** An amount typed under "Other": the server works out what it buys and the words for it, a moment after typing stops. */
function useTypedOffer(typed: string, minCents: number) {
  const [state, setState] = useState<{ offer: DepositOffer | null; error: string | null }>({ offer: null, error: null })
  useEffect(() => {
    setState({ offer: null, error: null })
    if (!typed) return
    const cents = Number(typed) * 100
    if (cents < minCents) { setState({ offer: null, error: `At least ${dollars(minCents)}` }); return }
    let live = true
    const t = setTimeout(() => {
      api<DepositOffer>(`/api/client/join/deposit/offer?cents=${cents}`)
        .then((offer) => { if (live) setState({ offer, error: null }) })
        .catch((e) => { if (live) setState({ offer: null, error: (e as Error).message }) })
    }, 350)
    return () => { live = false; clearTimeout(t) }
  }, [typed, minCents])
  return state
}

export function BusinessStep({ d, onSaved }: { d: JoinState; onSaved: () => void }) {
  const toast = useToast()
  const b = d.business
  const [f, setF] = useState({
    name: b.name || '', legal_name: b.legal_name || '', entity_type: b.entity_type || '', entity_state: b.entity_state || 'AZ',
    category_id: b.category_id, job_type_ids: b.job_type_ids || [], roc_license: b.roc_license || '', mailing_address: b.mailing_address || '',
    notice_email: b.notice_email || '', website: b.website || '', facebook_page: b.facebook_page || '',
    lead_contact_name: b.lead_contact_name || '', lead_contact_phone: b.lead_contact_phone || '', lead_contact_email: b.lead_contact_email || '',
  })
  const [busy, setBusy] = useState(false)
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value })
  const toggleJob = (id: string) => setF({ ...f, job_type_ids: f.job_type_ids.includes(id) ? f.job_type_ids.filter((x) => x !== id) : [...f.job_type_ids, id] })
  const save = async () => {
    setBusy(true)
    try { await put('/api/client/join/business', f); onSaved() } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }
  return (
    <Panel>
      <div className="panel-header"><h2 className="panel-title">Your business</h2></div>
      <p className="muted" style={{ marginTop: 0 }}>The Terms are signed in your business's legal name, so these must match your state filing.</p>
      <div className="form-grid">
        <Field label="Business name, as customers know it" id="b-name" value={f.name} onChange={set('name')} />
        <Field label="Legal name" id="b-legal" value={f.legal_name} onChange={set('legal_name')} />
        <FormRow label="Kind of business" htmlFor="b-entity">
          <Select id="b-entity" value={f.entity_type} onChange={set('entity_type')}>
            <option value="">Choose one</option>
            {d.entity_types.map((t) => <option key={t} value={t}>{t}</option>)}
          </Select>
        </FormRow>
        <Field label="State it is registered in (two letters)" id="b-state" maxLength={2} value={f.entity_state} onChange={set('entity_state')} />
        {d.status === 'pending' && (
          <FormRow label="Trade" htmlFor="b-trade" help="One trade per sign-up. Tell us if you do more and we will add it.">
            <Select id="b-trade" value={f.category_id} onChange={(e) => setF({ ...f, category_id: e.target.value, job_type_ids: [] })}>
              {d.categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </FormRow>
        )}
        <Field label="Arizona ROC license number, if your trade needs one" id="b-roc" value={f.roc_license} onChange={set('roc_license')} />
        <Field label="Mailing address" id="b-addr" autoComplete="street-address" value={f.mailing_address} onChange={set('mailing_address')} />
        <Field label="Email for notices about your account" id="b-notice" type="email" value={f.notice_email} onChange={set('notice_email')} />
        <Field label="Website (or leave blank and give your Facebook page)" id="b-web" placeholder="https://" value={f.website} onChange={set('website')} />
        <Field label="Facebook page" id="b-fb" placeholder="https://facebook.com/" value={f.facebook_page} onChange={set('facebook_page')} />
      </div>
      {f.category_id === d.business.category_id && d.job_types.length > 0 && (
        <FormRow label="The jobs you take" htmlFor="b-jobs">
          <div className="filter-chips" id="b-jobs">
            {d.job_types.map((jt) => <button key={jt.id} type="button" className="filter-chip" aria-pressed={f.job_type_ids.includes(jt.id)} onClick={() => toggleJob(jt.id)}>{jt.name}</button>)}
          </div>
        </FormRow>
      )}
      <h3 className="section-title">Who receives leads</h3>
      <div className="form-grid">
        <Field label="Name" id="b-ln" value={f.lead_contact_name} onChange={set('lead_contact_name')} />
        <Field label="Mobile" id="b-lp" type="tel" value={f.lead_contact_phone} onChange={set('lead_contact_phone')} />
        <Field label="Email" id="b-le" type="email" value={f.lead_contact_email} onChange={set('lead_contact_email')} />
      </div>
      <div className="row-tight" style={{ marginTop: 'var(--s-5)' }}><Button onClick={save} disabled={busy}>{busy ? 'Saving' : 'Save and continue'}</Button></div>
    </Panel>
  )
}

